import { DefaultEuler, EulerOrder } from "./Euler";
import { MathUtil } from "./MathUtil";
import { IQuat } from "./Quat";
import { IVec3, Vec3 } from "./Vec3";

export type IMat4 = [
  a1: number,
  b1: number,
  c1: number,
  d1: number,
  a2: number,
  b2: number,
  c2: number,
  d2: number,
  a3: number,
  b3: number,
  c3: number,
  d3: number,
  a4: number,
  b4: number,
  c4: number,
  d4: number
];

export class Mat4 {
  static create(): IMat4 {
    return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  }

  static createTranslation(v: IVec3): IMat4;
  static createTranslation(x: number, y: number, z: number): IMat4;
  static createTranslation(vx: number | IVec3, y?: number, z?: number): IMat4 {
    if (typeof vx === "number")
      return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, vx, y!, z!, 1];
    return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, vx.x, vx.y, vx.z, 1];
  }

  static createTranslationFromVec(v: IVec3): IMat4 {
    return this.createTranslation(v.x, v.y, v.z);
  }

  static createBasis(xAxis: IVec3, yAxis: IVec3, zAxis: IVec3, translation?: IVec3): IMat4 {
    const t = translation || Vec3.ZERO;
    return [
      xAxis.x, xAxis.y, xAxis.z, 0,
      yAxis.x, yAxis.y, yAxis.z, 0,
      zAxis.x, zAxis.y, zAxis.z, 0,
      t.x, t.y, t.z, 1
    ];
  }

  static createLookAt(eye: IVec3, target: IVec3, up: IVec3, translation?: IVec3): IMat4 {
    const targetToEye = Vec3.to(target, eye);
    const _z = Vec3.len2(targetToEye) === 0 ? Vec3.from(0, 0, 1) : Vec3.normalized(targetToEye);
    const _x = Vec3.crossNormalized(up, _z);
    const _y = Vec3.crossNormalized(_z, _x);

    return Mat4.fromArray([
      _x.x, _x.y, _x.z, 0,
      _y.x, _y.y, _y.z, 0,
      _z.x, _z.y, _z.z, 0,
      translation?.x || 0, translation?.y || 0, translation?.z || 0, 1
    ])
  }

  static getTranslation(m: IMat4): IVec3 {
    return {
      x: m[12],
      y: m[13],
      z: m[14],
    };
  }

  static clone(m: IMat4): IMat4 {
    return [...m];
  }

  static fromArray(numbers: number[]): IMat4 {
    return numbers.slice(0, 16) as IMat4;
  }

  static equals(a: IMat4, b: IMat4): boolean {
    if (a === b) return true;
    if (!a && b) return false;
    if (!b && a) return false;

    for (let i = 0; i < 16; i++) {
      if (a[i] !== b[i])
        return false;
    }
    return true;
  }

  static createRotationX(theta: number): IMat4 {
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    return [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1];
  }

  static createRotationY(theta: number): IMat4 {
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    return [
      c, 0, -s, 0,
      0, 1, 0, 0,
      s, 0, c, 0,
      0, 0, 0, 1
    ];
  }

  static createRotationZ(theta: number): IMat4 {
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    return [c, s, 0, 0, -s, c, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  }

  static createScale(x: number, y: number, z: number): IMat4 {
    return [x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0, 0, 0, 0, 1];
  }

  static createRotation(x: number, y: number, z: number, order: EulerOrder = DefaultEuler): IMat4 {
    const a = Math.cos(x);
    const b = Math.sin(x);
    const c = Math.cos(y);
    const d = Math.sin(y);
    const e = Math.cos(z);
    const f = Math.sin(z);

    switch (order) {
      case "XYZ": {
        const ae = a * e;
        const af = a * f;
        const be = b * e;
        const bf = b * f;

        return [c * e, af + be * d, bf - ae * d, 0, -c * f, ae - bf * d, be + af * d, 0, d, -b * c, a * c, 0, 0, 0, 0, 1];
      }
      case "YXZ": {
        const ce = c * e;
        const cf = c * f;
        const de = d * e;
        const df = d * f;

        return [ce + df * b, a * f, cf * b - de, 0, de * b - cf, a * e, df + ce * b, 0, a * d, - b, a * c, 0, 0, 0, 0, 1];
      }
      default:
        throw new Error("not supported so far!");
    }
  }

  static createTransform(params: { translation?: Partial<IVec3>, rotation?: Partial<IVec3> | IQuat, scale?: Partial<IVec3> | number }): IMat4;
  static createTransform(x: number, y: number, z: number, rx: number, ry: number, rz: number, sx: number, sy: number, sz: number): IMat4;
  static createTransform(x: any, y?: number, z?: number, rx?: number, ry?: number, rz?: number, sx?: number, sy?: number, sz?: number): IMat4 {
    if (typeof x === "number") {
      const t = Mat4.createTranslation(x, y!, z!);
      const r = Mat4.createRotation(rx!, ry!, rz!);
      const s = Mat4.createScale(sx!, sy!, sz!);
      return Mat4.mul(Mat4.mul(t, r), s);
    } else {
      const matrices: IMat4[] = [];
      const { translation, rotation, scale } = x;

      if (scale) {
        const { x = 1, y = 1, z = 1 } = typeof scale === "number" ? { x: scale, y: scale, z: scale } : scale;
        matrices.push(Mat4.createScale(x, y, z));
      }
      if (rotation) {
        if (rotation.w !== undefined) {
          matrices.push(Mat4.rotationFromQuat(rotation as IQuat));
        } else {
          const { x = 0, y = 0, z = 0 } = rotation;
          matrices.push(Mat4.createRotation(x, y, z));
        }
      }
      if (translation) {
        const { x = 0, y = 0, z = 0 } = translation;
        matrices.push(Mat4.createTranslation(x, y, z));
      }

      let ret = matrices.length === 0 ? Mat4.IDENTITY : matrices[0];
      for (let i = 1; i < matrices.length; i++) {
        ret = Mat4.mul(matrices[i], ret);
      }
      return ret;
    }
  }

  // static createTransform(x: number, y: number, z: number, rx: number, ry: number, rz: number, sx: number, sy: number, sz: number): IMat4 {
  //   const t = Mat4.createTranslation(x, y, z);
  //   const r = Mat4.createRotation(rx, ry, rz);
  //   const s = Mat4.createScale(sx, sy, sz);
  //   // return Mat4.mul(Mat4.mul(t, r), s);
  //   return Mat4.mul(Mat4.mul(t, r), s);
  // }

  static createTransform2(x: number, y: number, z: number, rx: number, ry: number, rz: number, sx: number, sy: number, sz: number): IMat4 {
    const t = Mat4.createTranslation(x, y, z);
    const r = Mat4.createRotation(rx, ry, rz);
    const s = Mat4.createScale(sx, sy, sz);
    // return Mat4.mul(Mat4.mul(t, r), s);
    return Mat4.mul(Mat4.mul(r, t), s);
  }

  static mul(a: IMat4, b: IMat4): IMat4 {
    const [a11, a21, a31, a41, a12, a22, a32, a42, a13, a23, a33, a43, a14, a24, a34, a44] = a;
    const [b11, b21, b31, b41, b12, b22, b32, b42, b13, b23, b33, b43, b14, b24, b34, b44] = b;

    return [
      a11 * b11 + a12 * b21 + a13 * b31 + a14 * b41,
      a21 * b11 + a22 * b21 + a23 * b31 + a24 * b41,
      a31 * b11 + a32 * b21 + a33 * b31 + a34 * b41,
      a41 * b11 + a42 * b21 + a43 * b31 + a44 * b41,

      a11 * b12 + a12 * b22 + a13 * b32 + a14 * b42,
      a21 * b12 + a22 * b22 + a23 * b32 + a24 * b42,
      a31 * b12 + a32 * b22 + a33 * b32 + a34 * b42,
      a41 * b12 + a42 * b22 + a43 * b32 + a44 * b42,

      a11 * b13 + a12 * b23 + a13 * b33 + a14 * b43,
      a21 * b13 + a22 * b23 + a23 * b33 + a24 * b43,
      a31 * b13 + a32 * b23 + a33 * b33 + a34 * b43,
      a41 * b13 + a42 * b23 + a43 * b33 + a44 * b43,

      a11 * b14 + a12 * b24 + a13 * b34 + a14 * b44,
      a21 * b14 + a22 * b24 + a23 * b34 + a24 * b44,
      a31 * b14 + a32 * b24 + a33 * b34 + a34 * b44,
      a41 * b14 + a42 * b24 + a43 * b34 + a44 * b44,
    ];
  }

  static muls(...rest: (IMat4 | undefined)[]): IMat4 {
    return Mat4.mulsArray(rest);
  }

  static mulsArray(matrices: (IMat4 | undefined)[]): IMat4 {
    if (matrices.length < 1)
      return Mat4.IDENTITY;

    let current = matrices[matrices.length - 1] || Mat4.IDENTITY;
    for (let i = matrices.length - 2; i >= 0; i--) {
      const m = matrices[i];
      if (m)
        current = Mat4.mul(m, current);
    }
    return current;
  }

  static rotationFromQuat(quat: IQuat): IMat4 { // compose...
    const sx = 1.0;
    const sy = 1.0;
    const sz = 1.0;

    const px = 0.0;
    const py = 0.0;
    const pz = 0.0;

    const { x, y, z, w } = quat;
    const x2 = x + x;
    const y2 = y + y;
    const z2 = z + z;
    const xx = x * x2;
    const xy = x * y2;
    const xz = x * z2;
    const yy = y * y2;
    const yz = y * z2;
    const zz = z * z2;
    const wx = w * x2;
    const wy = w * y2;
    const wz = w * z2;

    return [
      (1 - (yy + zz)) * sx,
      (xy + wz) * sx,
      (xz - wy) * sx,
      0,

      (xy - wz) * sy,
      (1 - (xx + zz)) * sy,
      (yz + wx) * sy,
      0,

      (xz + wy) * sz,
      (yz - wx) * sz,
      (1 - (xx + yy)) * sz,
      0,

      px,
      py,
      pz,
      1
    ]
  }

  static calcEuler(m: IMat4, order: EulerOrder = DefaultEuler): IVec3 {
    // order: xyz
    let x: number;
    let y: number;
    let z: number;

    const m11 = m[0], m12 = m[4], m13 = m[8];
    const m21 = m[1], m22 = m[5], m23 = m[9];
    const m31 = m[2], m32 = m[6], m33 = m[10];

    switch (order) {
      case "XYZ": {
        y = Math.asin(MathUtil.clamp(m13, - 1, 1));

        // console.log("m13", m13);
        if (Math.abs(m13) < 0.9999999) {
          x = Math.atan2(- m23, m33);
          z = Math.atan2(- m12, m11);
        } else {
          x = Math.atan2(m32, m22);
          z = 0;
        }
        return Vec3.from(x, y, z);
      }
      case "YXZ": {
        x = Math.asin(- MathUtil.clamp(m23, - 1, 1));
        if (Math.abs(m23) < 0.9999999) {
          y = Math.atan2(m13, m33);
          z = Math.atan2(m21, m22);
        } else {
          y = Math.atan2(- m31, m11);
          z = 0;
        }
        return Vec3.from(x, y, z);
      }
      default:
        throw new Error("Not supported so far!");
    }
  }

  static fromBasis(x: IVec3, y: IVec3, z: IVec3, t: IVec3 = Vec3.ZERO): IMat4 {
    return [
      x.x, x.y, x.z, 0,
      y.x, y.y, y.z, 0,
      z.x, z.y, z.z, 0,
      t.x, t.y, t.z, 1
    ]
  }

  static inverse(m: IMat4): IMat4 {

    // based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm
    const n11 = m[0], n21 = m[1], n31 = m[2], n41 = m[3];
    const n12 = m[4], n22 = m[5], n32 = m[6], n42 = m[7];
    const n13 = m[8], n23 = m[9], n33 = m[10], n43 = m[11];
    const n14 = m[12], n24 = m[13], n34 = m[14], n44 = m[15];

    const t11 = n23 * n34 * n42 - n24 * n33 * n42 + n24 * n32 * n43 - n22 * n34 * n43 - n23 * n32 * n44 + n22 * n33 * n44;
    const t12 = n14 * n33 * n42 - n13 * n34 * n42 - n14 * n32 * n43 + n12 * n34 * n43 + n13 * n32 * n44 - n12 * n33 * n44;
    const t13 = n13 * n24 * n42 - n14 * n23 * n42 + n14 * n22 * n43 - n12 * n24 * n43 - n13 * n22 * n44 + n12 * n23 * n44;
    const t14 = n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34;

    const det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14;

    if (det === 0)
      return Mat4.EMPTY;

    const detInv = 1 / det;

    return [
      t11 * detInv,
      (n24 * n33 * n41 - n23 * n34 * n41 - n24 * n31 * n43 + n21 * n34 * n43 + n23 * n31 * n44 - n21 * n33 * n44) * detInv,
      (n22 * n34 * n41 - n24 * n32 * n41 + n24 * n31 * n42 - n21 * n34 * n42 - n22 * n31 * n44 + n21 * n32 * n44) * detInv,
      (n23 * n32 * n41 - n22 * n33 * n41 - n23 * n31 * n42 + n21 * n33 * n42 + n22 * n31 * n43 - n21 * n32 * n43) * detInv,

      t12 * detInv,
      (n13 * n34 * n41 - n14 * n33 * n41 + n14 * n31 * n43 - n11 * n34 * n43 - n13 * n31 * n44 + n11 * n33 * n44) * detInv,
      (n14 * n32 * n41 - n12 * n34 * n41 - n14 * n31 * n42 + n11 * n34 * n42 + n12 * n31 * n44 - n11 * n32 * n44) * detInv,
      (n12 * n33 * n41 - n13 * n32 * n41 + n13 * n31 * n42 - n11 * n33 * n42 - n12 * n31 * n43 + n11 * n32 * n43) * detInv,

      t13 * detInv,
      (n14 * n23 * n41 - n13 * n24 * n41 - n14 * n21 * n43 + n11 * n24 * n43 + n13 * n21 * n44 - n11 * n23 * n44) * detInv,
      (n12 * n24 * n41 - n14 * n22 * n41 + n14 * n21 * n42 - n11 * n24 * n42 - n12 * n21 * n44 + n11 * n22 * n44) * detInv,
      (n13 * n22 * n41 - n12 * n23 * n41 - n13 * n21 * n42 + n11 * n23 * n42 + n12 * n21 * n43 - n11 * n22 * n43) * detInv,

      t14 * detInv,
      (n13 * n24 * n31 - n14 * n23 * n31 + n14 * n21 * n33 - n11 * n24 * n33 - n13 * n21 * n34 + n11 * n23 * n34) * detInv,
      (n14 * n22 * n31 - n12 * n24 * n31 - n14 * n21 * n32 + n11 * n24 * n32 + n12 * n21 * n34 - n11 * n22 * n34) * detInv,
      (n12 * n23 * n31 - n13 * n22 * n31 + n13 * n21 * n32 - n11 * n23 * n32 - n12 * n21 * n33 + n11 * n22 * n33) * detInv
    ];
  }

  static EMPTY: IMat4 = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  static SCALE0: IMat4 = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1];
  static IDENTITY: IMat4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
}
